import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ScanBarcode, Keyboard } from "lucide-react";

export function BarcodeScan({ open, onClose, onResult, title = "Code scannen" }) {
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const scannerRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!open || manualMode) return;
    let cancelled = false;
    const start = async () => {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");
        const scanner = new Html5Qrcode("barcode-reader", {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13, Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E, Html5QrcodeSupportedFormats.DATA_MATRIX,
          ],
        });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 140 } },
          (text) => {
            if (cancelled) return;
            scanner.stop().catch(() => {});
            onResult(text);
            onClose();
          },
          () => {}
        );
        startedRef.current = true;
      } catch (e) {
        setError("Kamera nicht verfügbar. Bitte Code manuell eingeben.");
        setManualMode(true);
      }
    };
    const t = setTimeout(start, 150);
    return () => {
      cancelled = true;
      clearTimeout(t);
      if (startedRef.current && scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        startedRef.current = false;
      }
    };
  }, [open, manualMode]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setManualMode(false); setError(""); } onClose(); }}>
      <DialogContent data-testid="barcode-scan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanBarcode className="w-5 h-5" /> {title}</DialogTitle></DialogHeader>
        {!manualMode ? (
          <div className="space-y-3">
            <div id="barcode-reader" className="w-full rounded-xl overflow-hidden bg-zinc-950 min-h-[220px]" data-testid="barcode-reader" />
            <p className="text-xs text-zinc-500 text-center">EAN, Barcode, QR-Code oder Datamatrix vor die Kamera halten.</p>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <Button variant="outline" className="w-full min-h-[44px]" onClick={() => setManualMode(true)} data-testid="barcode-manual-switch">
              <Keyboard className="w-4 h-4 mr-2" /> Manuell eingeben
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Input autoFocus value={manual} onChange={(e) => setManual(e.target.value)} placeholder="EAN / Seriennummer / IMEI eingeben…"
              data-testid="barcode-manual-input" className="font-mono"
              onKeyDown={(e) => { if (e.key === "Enter" && manual.trim()) { onResult(manual.trim()); onClose(); } }} />
            <Button className="w-full bg-zinc-900 hover:bg-zinc-800 text-white min-h-[44px]" disabled={!manual.trim()}
              onClick={() => { onResult(manual.trim()); onClose(); }} data-testid="barcode-manual-submit">
              Übernehmen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
